import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PlainButton } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { CompletePurpleIcon } from '../../../icons';
import { getLedgerIcon } from '../../icon';
import type { Subcategory } from './types';

const SuggestionGridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  grid-gap: 16px;
`;

type SuggestionPanelProps = {
  label: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
};

const SuggestionPanelWithRef: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  SuggestionPanelProps
> = ({ label, icon, isSelected, onClick }, ref) => (
  <div
    css={css`
      width: 100%;
    `}
  >
    <PlainButton
      ref={ref}
      data-testid="Suggestion"
      onClick={onClick}
      css={css`
        width: 100%;

        &:focus {
          color: ${colors.blue};
        }

        @media (hover) {
          &:hover {
            color: ${colors.blue};
          }
        }
      `}
    >
      <div
        css={css`
          text-align: center;
        `}
      >
        <div
          css={css`
            background-color: ${colors.background};
            border-radius: 8px;
            border: ${isSelected ? `1px solid ${colors.blue}` : null};

            position: relative;
            width: 100%;
            height: 80px;
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          {icon}
          {isSelected && (
            <CompletePurpleIcon
              css={css`
                position: absolute;
                right: 0;
                bottom: 0;
                display: block;
              `}
            />
          )}
        </div>
        <div
          css={css`
            margin-top: 8px;
          `}
        >
          {label}
        </div>
      </div>
    </PlainButton>
  </div>
);

const SuggestionPanel = React.forwardRef(SuggestionPanelWithRef);

type SuggestionSelectorProps = {
  suggestions: Subcategory[];
  selectedSubcategory?: Subcategory;
  onSubcategorySelected: (suggestion: Subcategory) => void;
  onOtherSelected: () => void;
  shouldFocus: boolean;
};

export const SuggestionSelector: React.FC<SuggestionSelectorProps> = ({
  suggestions,
  selectedSubcategory,
  onSubcategorySelected,
  onOtherSelected,
  shouldFocus,
}: SuggestionSelectorProps) => {
  return (
    <SuggestionGridWrapper>
      {suggestions.map(suggestion => (
        <SuggestionPanel
          key={suggestion.fid}
          label={suggestion.title}
          icon={getLedgerIcon(suggestion.icon)}
          isSelected={suggestion === selectedSubcategory}
          onClick={() => onSubcategorySelected(suggestion)}
        />
      ))}
      <SuggestionPanel
        label="Velg en annen"
        icon={getLedgerIcon('Other')}
        isSelected={false}
        onClick={onOtherSelected}
        ref={ele => shouldFocus && ele?.focus()}
      />
    </SuggestionGridWrapper>
  );
};
